import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Archive, Edit3, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useTailoredSupplyListQuery, useTailoredSupplyTableReferences } from '../../hooks';
import {
  isTailoredSupplySortProperty,
  resolveTailoredSupplyReferenceNames,
  tailoredSupplyPageSizes,
  TailoredSupplySortDirection,
  type TailoredSupplyItem,
} from '../../model';
import { useTailoredSupplyListRouteFilters } from '../../useRouteFilters';
import { TailoredSupplyBadgeList, TailoredSupplyNameCell } from './TailoredSupplyTableCells';
import { TailoredSupplyInfoList } from './TailoredSupplyInfoList';

export function TailoredSupplyTable() {
  const { can } = useShigoPermissions();
  const canEdit = can(ShigoPermission.TargetingEdit);
  const canArchive = can(ShigoPermission.TargetingArchive);
  const routeFilters = useTailoredSupplyListRouteFilters();
  const { filter } = routeFilters;
  const query = useTailoredSupplyListQuery(filter);
  const references = useTailoredSupplyTableReferences();
  const search = useDebouncedValue(filter.name, routeFilters.setSearch, 800);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TailoredSupplyItem>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('tailoredSupply.name'),
          headerClassName: 'w-72 pl-4',
          className: 'w-72 pl-4 font-medium',
          sortable: true,
          sortKey: 'name',
          cell: (item) => <TailoredSupplyNameCell item={item} />,
        },
        {
          id: 'apps',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('tailoredSupply.apps'),
          headerClassName: 'w-20',
          className: 'w-20 tabular-nums',
          value: (item) => String(item.appsCount),
        },
        {
          id: 'domains',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('tailoredSupply.domains'),
          headerClassName: 'w-24',
          className: 'w-24 tabular-nums',
          value: (item) => String(item.domainsCount),
        },
        {
          id: 'publisher',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('tailoredSupply.publisher'),
          headerClassName: 'w-56',
          className: 'w-56',
          value: (item) => item.publishers.join(', '),
        },
        {
          id: 'platform',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('tailoredSupply.platform'),
          headerClassName: 'w-52',
          className: 'w-52',
          cell: (item) => (
            <TailoredSupplyInfoList
              label={dashboardT('tailoredSupply.platformInfo', { name: item.name })}
              values={resolveTailoredSupplyReferenceNames(
                item.appPlatforms,
                references.platforms.data ?? [],
              )}
            />
          ),
        },
        {
          id: 'iabCategories',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('tailoredSupply.iabCategories'),
          headerClassName: 'w-60',
          className: 'w-60',
          cell: (item) => (
            <TailoredSupplyInfoList
              label={dashboardT('tailoredSupply.iabCategoriesInfo', { name: item.name })}
              values={resolveTailoredSupplyReferenceNames(
                item.appIabCategories,
                references.iabCategories.data ?? [],
              )}
            />
          ),
        },
        {
          id: 'languages',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('tailoredSupply.languages'),
          headerClassName: 'w-36',
          className: 'w-36',
          cell: (item) => <TailoredSupplyBadgeList values={item.appLanguages} variant="info" />,
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          header: '',
          cell: (item) =>
            canEdit || canArchive ? (
              <DashboardTableRowActions
                label={dashboardT('tailoredSupply.openActions', { name: item.name })}
              >
                {canEdit ? (
                  <DashboardTableRowActionItem disabled>
                    <Edit3 className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('tailoredSupply.edit')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canArchive ? (
                  <DashboardTableRowActionItem disabled>
                    <Archive className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('tailoredSupply.archive')}
                  </DashboardTableRowActionItem>
                ) : null}
              </DashboardTableRowActions>
            ) : null,
        },
      ]),
    [canArchive, canEdit, references.iabCategories.data, references.platforms.data],
  );
  return (
    <DashboardDataTable
      title={dashboardT('tailoredSupply.title')}
      tableLabel={dashboardT('tailoredSupply.title')}
      columns={columns}
      items={query.data?.content ?? []}
      getRowKey={(item) => item.id}
      loading={query.isLoading}
      loadingMessage={dashboardT('tailoredSupply.loading')}
      errorMessage={query.isError ? dashboardT('tailoredSupply.loadError') : undefined}
      emptyMessage={dashboardT('tailoredSupply.empty')}
      minWidthClassName="min-w-[1320px]"
      toolbar={
        <>
          <DashboardTableSearchInput
            aria-label={dashboardT('tailoredSupply.search')}
            placeholder={dashboardT('tailoredSupply.search')}
            value={search.draft}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
          />
          {canEdit ? (
            <Button asChild>
              <Link to="/supply/tailored-supply/create">
                <Plus className="h-4 w-4" />
                {dashboardT('tailoredSupply.add')}
              </Link>
            </Button>
          ) : null}
        </>
      }
      pagination={{
        value: {
          page: filter.page,
          pageSize: filter.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: tailoredSupplyPageSizes,
        disabled: query.isFetching,
        onChange: (next) => {
          routeFilters.setPagination(next.page, next.pageSize);
        },
      }}
      sort={{
        value: filter.sortProperty
          ? {
              key: filter.sortProperty,
              direction:
                filter.sortDirection === TailoredSupplySortDirection.Ascending
                  ? DashboardDataTableSortDirection.Asc
                  : DashboardDataTableSortDirection.Desc,
            }
          : undefined,
        onChange: (next) => {
          if (!next) {
            routeFilters.setSort(null, filter.sortDirection);
            return;
          }
          if (!isTailoredSupplySortProperty(next.key)) return;
          routeFilters.setSort(
            next.key,
            next.direction === DashboardDataTableSortDirection.Asc
              ? TailoredSupplySortDirection.Ascending
              : TailoredSupplySortDirection.Descending,
          );
        },
      }}
    />
  );
}
